
/**
 * Module dependencies.
 */

import { Component, ComponentType, ReactNode } from 'react';
import sentryClient from '../sentry-client';

/**
 * Export `ErrorInfo` type.
 */

export type ErrorInfo = {
  componentStack: string;
};

/**
 * Export `ErrorProps` type.
 */

export type ErrorProps = {
  error: Error | null | undefined;
  errorInfo: ErrorInfo | null | undefined;
  sentryEventId: string | undefined;
};

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  errorComponent: ComponentType<ErrorProps>;
};

/**
 * `State` type.
 */

type State = {
  error: Error | null | undefined;
  errorInfo: ErrorInfo | null | undefined;
  hasError: boolean;
  sentryEventId: string | undefined;
};

/**
 * Export `ErrorBoundary` component.
 */

export class ErrorBoundary extends Component<Props, State> {

  /**
   * Initial state.
   */

  state = {
    error: null,
    errorInfo: null,
    hasError: false,
    sentryEventId: undefined
  };

  /**
   * Component did catch..
   */

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const sentryEventId = sentryClient.captureException({
      error,
      errorInfo
    }) as string | undefined;

    this.setState({ error, errorInfo, hasError: true, sentryEventId });
  }

  /**
   * Render.
   */

  render(): ReactNode {
    const { error, errorInfo, hasError, sentryEventId } = this.state;

    if (hasError) {
      const ErrorComponent = this.props.errorComponent;

      return (
        <ErrorComponent
          error={error}
          errorInfo={errorInfo}
          sentryEventId={sentryEventId}
        />
      );
    }

    return this.props.children;
  }

}
